import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  Film,
  UsersRound,
  Mail,
  ClipboardCheck,
  Newspaper,
  MessagesSquare,
  ChartNoAxesCombined,
  ShieldCheck,
  LogOut,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { loginRedirectUri } from "@/lib/keycloak";
import { SEO } from "@/lib/seo";
import { PulseBoardMark } from "@/components/board/PulseBoardMark";
import "@/styles/admin-home.css";

const AREAS = [
  {
    path: "membership",
    title: "Membership",
    text: "Review applications and manage access to the clinician network.",
    icon: UsersRound,
  },
  {
    path: "invites",
    title: "Specialist invitations",
    text: "Manage invitations to confirm referral availability.",
    icon: Mail,
  },
  {
    path: "corrections",
    title: "Directory corrections",
    text: "Review submitted updates to specialist information.",
    icon: ClipboardCheck,
  },
  {
    path: "brief",
    title: "Referral brief",
    text: "Prepare and manage the Canberra referral briefing.",
    icon: Newspaper,
  },
  {
    path: "rounds",
    title: "Rounds moderation",
    text: "Manage communities and review clinician discussions.",
    icon: MessagesSquare,
  },
  {
    path: "access-report",
    title: "Access reports",
    text: "Prepare and manage specialist access reporting.",
    icon: ChartNoAxesCombined,
  },
];

export default function AdminHome() {
  const auth = useAuth();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const allowed =
    auth.isAuthenticated && auth.hasAnyRole("super_admin", "pulseboard_admin");
  const from = location.state?.from;
  const destination =
    typeof from === "string" &&
    /^\/admin\/(membership|corrections|invites|tiles|brief|rounds|access-report)(\?.*)?$/.test(
      from,
    )
      ? from
      : "/admin";
  async function signIn() {
    setBusy(true);
    setError("");
    try {
      await auth.login({ redirectUri: loginRedirectUri(destination) });
    } catch {
      setError("Sign-in could not be opened. Please try again.");
    } finally {
      setBusy(false);
    }
  }
  async function signOut() {
    setBusy(true);
    setError("");
    try {
      await auth.logout({ redirectUri: loginRedirectUri("/admin/login") });
    } catch {
      setError("Sign-out could not be completed. Please try again.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="pb-admin-home">
      <SEO
        title="Admin · PulseBoard"
        description="PulseBoard administration and CPD publishing workspace."
        path="/admin"
        noindex
      />
      <header className="pb-admin-home-header">
        <Link to="/admin" className="pb-admin-home-brand">
          <PulseBoardMark />
          <span>
            Pulse<span>Board</span>
            <small>ADMINISTRATION</small>
          </span>
        </Link>
        <div>
          <Link to="/board">
            Clinician Board <ArrowRight size={15} aria-hidden="true" />
          </Link>
          {auth.isAuthenticated && (
            <button disabled={busy} onClick={signOut}>
              <LogOut size={15} aria-hidden="true" />
              Sign out
            </button>
          )}
        </div>
      </header>
      <main id="admin-main">
        {error && (
          <p className="pb-admin-home-error" role="alert">
            {error}
          </p>
        )}
        {auth.isLoading ? (
          <section className="pb-admin-entry" role="status">
            <ShieldCheck size={40} aria-hidden="true" />
            <h1>Opening your workspace…</h1>
            <p>Checking your administrator access.</p>
          </section>
        ) : !allowed ? (
          <section className="pb-admin-entry">
            <div className="pb-admin-entry-art" aria-hidden="true">
              <ShieldCheck size={64} strokeWidth={1.2} />
            </div>
            <p className="pb-admin-home-kicker">THE WORK BEHIND THE NETWORK</p>
            <h1>PulseBoard Admin</h1>
            <p>
              One workspace for specialist learning, your clinician community
              and the details that keep PulseBoard useful.
            </p>
            {auth.isAuthenticated ? (
              <>
                <div className="pb-admin-home-error" role="alert">
                  <strong>Administrator access required</strong>
                  <p>
                    This account does not have administrator access. An existing
                    administrator needs to authorise admin access before you can
                    enter.
                  </p>
                </div>
                <button
                  className="pb-admin-home-primary"
                  disabled={busy}
                  onClick={signOut}
                >
                  Sign out to use another account{" "}
                  <ArrowRight size={17} aria-hidden="true" />
                </button>
              </>
            ) : (
              <>
                <button
                  className="pb-admin-home-primary"
                  disabled={busy}
                  onClick={signIn}
                >
                  {busy ? "Opening sign-in…" : "Sign in to Admin"}
                  <ArrowRight size={17} aria-hidden="true" />
                </button>
                {auth.initError && (
                  <p role="alert">
                    The sign-in service could not initialise. Try signing in
                    again.
                  </p>
                )}
                <small>
                  Use your authorised PulseBoard administrator account.
                </small>
              </>
            )}
          </section>
        ) : (
          <>
            <section className="pb-admin-home-intro">
              <p className="pb-admin-home-kicker">
                PULSEBOARD / ADMIN WORKSPACE
              </p>
              <h1>Behind better practice.</h1>
              <p>
                Publish useful learning. Support your specialists. Keep the
                network moving.
              </p>
            </section>
            <section
              className="pb-admin-studio-feature"
              aria-labelledby="admin-studio-title"
            >
              <div>
                <p className="pb-admin-home-kicker">FIVE MINUTE CPD</p>
                <h2 id="admin-studio-title">
                  Great teaching.
                  <br />
                  Ready for your audience.
                </h2>
                <p>
                  Prepare a recording brief, review a finished session and
                  publish it to the clinician channel.
                </p>
                <Link className="pb-admin-home-primary" to="/admin/tiles">
                  Open CPD Studio <ArrowRight size={18} aria-hidden="true" />
                </Link>
                <small>
                  Publish using a YouTube or Vimeo link. Direct video uploads
                  are not available yet.
                </small>
              </div>
              <div className="pb-admin-studio-art" aria-hidden="true">
                <Film size={80} strokeWidth={1} />
                <span>
                  5<small>MINUTE CPD</small>
                </span>
              </div>
            </section>
            <section aria-labelledby="admin-tools-title">
              <div className="pb-admin-tools-heading">
                <h2 id="admin-tools-title">Manage your platform</h2>
                <span>People, content & community</span>
              </div>
              <div className="pb-admin-tools-grid">
                {AREAS.map(({ path, title, text, icon: Icon }) => (
                  <Link
                    key={path}
                    to={`/admin/${path}`}
                    className="pb-admin-tool"
                  >
                    <Icon size={25} strokeWidth={1.5} aria-hidden="true" />
                    <h3>{title}</h3>
                    <p>{text}</p>
                    <span>
                      Open workspace <ArrowRight size={15} aria-hidden="true" />
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          </>
        )}
      </main>
      <footer>
        <ShieldCheck size={15} aria-hidden="true" /> Restricted to authorised
        administrators <span>PulseBoard · Amida Health</span>
      </footer>
    </div>
  );
}
